import React from 'react';
import BaseTable from '@app/components/common/Table/BaseTable';
import { Badge, Table, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { OrderModel, ProductItemsProp } from '@app/interfaces/order';
import { formatMoney } from '@app/utils/utils';
import ProductItemThumbnail from '@app/components/order/ProductItemThumbnail';
import './OrderInfo.css';

const { Text } = Typography;

interface OrderInfoProps {
  record: OrderModel;
}

const TOTAL_COLS = 9; // total column count in the table (gồm cột ảnh sản phẩm)

const columns: ColumnsType<ProductItemsProp> = [
  {
    title: '#',
    key: 'index',
    width: 36,
    align: 'center',
    render: (_: any, __: any, index: number) => (
      <Text type="secondary" className="tw-text-xs">{index + 1}</Text>
    ),
  },
  {
    title: 'Ảnh',
    dataIndex: 'image_url',
    key: 'image_url',
    width: 64,
    align: 'center',
    render: (imageUrl: string | undefined, item: ProductItemsProp) => (
      <ProductItemThumbnail imageUrl={imageUrl} name={item.name} />
    ),
  },
  {
    title: 'Mã SP',
    dataIndex: 'product_code',
    key: 'product_code',
    width: 110,
    render: (v: string) => <Text code className="tw-text-xs">{v}</Text>,
  },
  {
    title: 'Tên sản phẩm',
    dataIndex: 'name',
    key: 'name',
    render: (v: string) => <Text strong>{v}</Text>,
  },
  {
    title: 'SL',
    dataIndex: 'qty',
    key: 'qty',
    width: 56,
    align: 'center',
    render: (v: number) => <Badge count={v} color="var(--primary-color)" overflowCount={9999} />,
  },
  {
    title: 'ĐVT',
    dataIndex: 'unit',
    key: 'unit',
    width: 64,
    align: 'center',
    render: (v: string) => <Text type="secondary">{v}</Text>,
  },
  {
    title: 'Giá niêm yết',
    dataIndex: 'selling_price',
    key: 'selling_price',
    align: 'right',
    width: 120,
    render: (v: number) => (
      <Text delete type="secondary" className="tw-text-xs">
        {formatMoney(Number(v))}
      </Text>
    ),
  },
  {
    title: 'Giá bán',
    dataIndex: 'price',
    key: 'price',
    align: 'right',
    width: 120,
    render: (v: number) => <Text type="success">{formatMoney(Number(v))}</Text>,
  },
  {
    title: 'Thành tiền',
    dataIndex: 'amount',
    key: 'amount',
    align: 'right',
    width: 130,
    render: (v: number) => (
      <Text strong className="tw-text-primary">
        {formatMoney(Number(v))}
      </Text>
    ),
  },
];

interface SummaryRowProps {
  label: string;
  value: number;
  labelClass?: string;
  valueClass?: string;
  bold?: boolean;
}

const SummaryRow: React.FC<SummaryRowProps> = ({ label, value, labelClass, valueClass, bold }) => (
  <Table.Summary.Row>
    <Table.Summary.Cell index={0} colSpan={TOTAL_COLS - 1} align="right">
      <Text strong={bold} className={labelClass}>
        {label}
      </Text>
    </Table.Summary.Cell>
    <Table.Summary.Cell index={1} align="right">
      <Text strong={bold} className={valueClass}>
        {formatMoney(value)}
      </Text>
    </Table.Summary.Cell>
  </Table.Summary.Row>
);

const OrderInfo: React.FC<OrderInfoProps> = ({ record }) => {
  const orderProducts: ProductItemsProp[] = record.items || [];
  const subtotal = orderProducts.reduce((s, p) => s + Number(p.amount ?? 0), 0);

  return (
    <BaseTable
      rowKey="product_code"
      columns={columns}
      dataSource={orderProducts}
      size="small"
      bordered
      pagination={false}
      locale={{ emptyText: 'Không có sản phẩm.' }}
      rowClassName={(_, i) => (i % 2 !== 0 ? 'order-info__row-alt' : '')}
      summary={() => (
        <Table.Summary fixed>
          <SummaryRow
            label="Tổng tiền hàng"
            value={subtotal}
            valueClass="tw-text-primary"
          />
          <SummaryRow
            label={`Chiết khấu (${record.discount_percent ?? 0}%)`}
            value={record.discount_amount ?? 0}
            valueClass="tw-text-error"
          />
          <SummaryRow
            label={`VAT (${record.vat_percent ?? 0}%)`}
            value={record.vat_amount ?? 0}
            valueClass="tw-text-warning"
          />
          <SummaryRow
            label="Phí giao hàng"
            value={record.shipping_fee ?? 0}
            valueClass="tw-text-on-surface-variant"
          />
          <SummaryRow
            label="Chi phí khác"
            value={record.other_amount ?? 0}
            valueClass="tw-text-on-surface-variant"
          />
          {Number(record.points_discount_amount) > 0 && (
            <SummaryRow
              label={`Trừ điểm tích luỹ (${record.points_used} điểm)`}
              value={-Number(record.points_discount_amount)}
              valueClass="tw-text-error"
            />
          )}
          <SummaryRow
            label="Tổng cộng"
            value={record.total_amount ?? 0}
            labelClass="tw-text-sm tw-font-semibold"
            valueClass="tw-text-[15px] tw-text-success-teal"
            bold
          />
        </Table.Summary>
      )}
    />
  );
};

export default OrderInfo;
